import { Conversation } from '@twilio/conversations'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Input, Button as MerinioButton, Dropdown } from 'ui'
import { FullScreen } from 'features/MerinioChat/components'
import { ConversationInfo } from './ConversationInfo'

import styles from '../../style.module.scss'

type Props = {
  setActiveConversation: (conversation: Conversation | null) => void
  displayName: string
  isLoading: boolean
  isAuthor: boolean
  conversation: Conversation
  handleDeleteConversation: () => Promise<void>
}

const ConversationNav: FC<Props> = ({ setActiveConversation, conversation, isLoading, isAuthor, displayName, handleDeleteConversation }): JSX.Element => {
  const { t } = useTranslation('legacy')

  const [editFriendlyNameScreen, setEditFriendlyNameScreen] = useState(false)
  const [conversationInfoScreen, setConversationInfoScreen] = useState(false)
  const [deleteScreenOpen, setDeleteScreenOpen] = useState(false)

  const [convoFriendlyName, setConvoFriendlyName] = useState(displayName ?? '')

  const convoName = useMemo(() => {
    if (displayName.length > 35) {
      return displayName.substring(0, 35) + '...'
    }
    return displayName
  }, [displayName])

  const isGroup = useMemo(() => {
    return conversation.attributes?.['type'] === 'group' ?? false
  }, [conversation])

  const handleConvoFriendlyNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setConvoFriendlyName(e.target.value)
  }

  return (
    <>
      <nav className={`d-flex justify-content-between align-items-center p-2 shadow-sm ${styles.nav}`}>
        <MerinioButton type="button" onClick={() => setActiveConversation(null)}>
          <i className="fa fa-angle-left"></i>
        </MerinioButton>
        <div className="d-flex justify-content-between align-items-center">
          {isAuthor ? (
            <>
              <Dropdown>
                <Dropdown.Toggle size="small" outlined color="light" title={displayName}>
                  {!isLoading && convoName}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {isGroup ? (
                    <>
                      <Dropdown.Item onClick={() => setConversationInfoScreen(true)}>{t('chat.CONVO.MENU.MANAGE')}</Dropdown.Item>
                      <Dropdown.Item onClick={() => setEditFriendlyNameScreen(true)}>{t('chat.CONVO.MENU.RENAME')}</Dropdown.Item>
                    </>
                  ) : null}
                  <Dropdown.Item onClick={() => setDeleteScreenOpen(true)}>{t('chat.CONVO.MENU.DELETE')}</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          ) : (
            <h6>{!isLoading && displayName}</h6>
          )}
        </div>
      </nav>
      <FullScreen isOpen={editFriendlyNameScreen} onClose={() => setEditFriendlyNameScreen(false)}>
        <div className="input-group mt-3">
          <Input onChange={handleConvoFriendlyNameChange} value={convoFriendlyName} type="text" placeholder={t('chat.CONVO.CUSTOM_NAME')} />
          <div className="input-group-append">
            <MerinioButton
              color="primary"
              type="button"
              onClick={async () => {
                await conversation.updateFriendlyName(convoFriendlyName)
                setEditFriendlyNameScreen(false)
              }}
            >
              {t('SUBMIT')}
            </MerinioButton>
          </div>
        </div>
      </FullScreen>
      <FullScreen isOpen={conversationInfoScreen} onClose={() => setConversationInfoScreen(false)}>
        <ConversationInfo conversation={conversation} />
      </FullScreen>
      <FullScreen isOpen={deleteScreenOpen} onClose={() => setDeleteScreenOpen(false)}>
        <Alert variant="danger" className={'mt-2'}>
          {t('chat.CONVO.DELETE_CONVERSATION.SUMMARY')}
        </Alert>
        <MerinioButton color="error" onClick={handleDeleteConversation}>
          {t('chat.CONVO.DELETE_CONVERSATION.PRIMARY_ACTION')}
        </MerinioButton>
      </FullScreen>
    </>
  )
}

export { ConversationNav }
